import React, { useEffect, useState } from 'react'

/* Atoms */
import Box from 'components_new/atoms/Box'
import Button from 'components_new/atoms/Button'
import Chip from 'components_new/atoms/Chip'
import DialogContent from 'components_new/atoms/Dialog/DialogContent'
import Divider from 'components_new/atoms/Divider'
import Text from 'components_new/atoms/Text'
import TextField from 'components_new/atoms/TextField'

/* Molecules */
import SimpleDialog from 'components_new/molecules/SimpleDialog'

/* Actions */
import { useObjectSizeMapperActions } from 'redux/actions/internal/ObjectSizeMapper'

/* Hooks */
import { useObjectSizeMappings } from 'redux/hooks/internal/ObjectSizeMapper'

/* Types */
import { NormalizedRentType } from 'types/GlobalNormalizedRent'

interface NormalizedRentMappingDialogProps {
  customerId: string
  open: boolean
  onClose: () => void
}

const ALL_TYPES = Object.values(NormalizedRentType)

const NormalizedRentMappingDialog = (
  props: NormalizedRentMappingDialogProps
) => {
  const { customerId, open, onClose } = props

  const {
    tryGetObjectSizeMappings,
    tryCreateObjectSizeMapping,
    tryDeleteObjectSizeMapping
  } = useObjectSizeMapperActions()

  const mappings = useObjectSizeMappings(customerId)

  const [inputs, setInputs] = useState<Record<string, string>>(() =>
    Object.fromEntries(ALL_TYPES.map((t) => [t, '']))
  )

  useEffect(() => {
    if (open) {
      tryGetObjectSizeMappings(customerId)
    }
  }, [open, customerId])

  const mappingsByType = (type: NormalizedRentType) =>
    mappings.filter((m) => m.boverket_type === type)

  const handleAdd = (type: NormalizedRentType) => {
    const value = inputs[type].trim()

    if (!value) return

    tryCreateObjectSizeMapping(customerId, { boverket_type: type, value })
    setInputs((prev) => ({ ...prev, [type]: '' }))
  }

  const handleKeyDown = (e: React.KeyboardEvent, type: NormalizedRentType) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleAdd(type)
    }
  }

  return (
    <SimpleDialog
      title="Normhyra mappningar"
      helperText="Ange kundens värden för varje lägenhetstyp. Flera värden per typ är tillåtna."
      open={open}
      onClose={onClose}
      hasClose
      fullWidth
      maxWidth="sm"
    >
      <DialogContent sx={{ p: 0 }}>
        {ALL_TYPES.map((type, index) => (
          <Box key={type}>
            {index > 0 && <Divider />}
            <Box sx={{ px: 3, py: 2 }}>
              <Text variant="body2" fontWeight="bold" sx={{ mb: 1 }}>
                {type}
              </Text>

              {mappingsByType(type).length > 0 && (
                <Box
                  sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}
                >
                  {mappingsByType(type).map((mapping) => (
                    <Chip
                      key={mapping.id}
                      label={mapping.value}
                      size="small"
                      onDelete={() =>
                        tryDeleteObjectSizeMapping(customerId, mapping.id)
                      }
                    />
                  ))}
                </Box>
              )}

              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  size="small"
                  placeholder="Lägg till värde..."
                  value={inputs[type]}
                  onChange={(e) =>
                    setInputs((prev) => ({ ...prev, [type]: e.target.value }))
                  }
                  onKeyDown={(e) => handleKeyDown(e, type)}
                  fullWidth
                />
                <Button
                  variant="outlined"
                  size="small"
                  disabled={!inputs[type].trim()}
                  onClick={() => handleAdd(type)}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  Lägg till
                </Button>
              </Box>
            </Box>
          </Box>
        ))}
      </DialogContent>
    </SimpleDialog>
  )
}

export default NormalizedRentMappingDialog
